import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentUser } from "../../redux/userRedux";
import { database, storage } from "../../firebase";
import { ref, onValue, off, remove, get, set } from "firebase/database";
import { ref as storageRef, deleteObject } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import useIsMobile from "../../hooks/useIsMobile";
import CaseTable from "./CaseTable";
import CaseListTable from "./CaseListTable";
import DeleteConfirmationModal from "../modals/DeleteConfirmationModal";
import CaseSearchBar from "./CaseSearchBar";
import MobileCaseTable from "./MobileCaseTable";
import { setSelectedCase } from "../../redux/casesRedux";
import { anatomyConstants } from "../../constants/anatomyConstants";
import { formatDateFromMillis } from "../../utils/dateUtils";

const CaseListPageView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const [adminViewMode, setAdminViewMode] = useState("none");

  const [caseList, setCaseList] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [showDeleteButtons, setShowDeleteButtons] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [isHardDeleteModalOpen, setIsHardDeleteModalOpen] = useState(false);
  const [isSoftDeleteModalOpen, setIsSoftDeleteModalOpen] = useState(false);

  const [caseToDelete, setCaseToDelete] = useState(null);

  const isMobile = useIsMobile();

  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("caseView") || "list"
  );

  const [currentPage, setCurrentPage] = useState(1);

  // 🔹 Page size options + persisted state in localStorage
  const pageSizeOptions = [10, 20, 50];
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem("caseItemsPerPage");
      const parsed = saved ? parseInt(saved, 10) : NaN;
      return !isNaN(parsed) && pageSizeOptions.includes(parsed) ? parsed : 10;
    } catch {
      return 10;
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [caseCache, setCaseCache] = useState({});
  const [caseTitleIndex, setCaseTitleIndex] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  // Track which case IDs have already been fetched/queued to prevent duplicate requests
  const fetchedTitleIdsRef = useRef(new Set());
  // Track if user has ever searched (for lazy loading titles)
  const hasSearchedRef = useRef(false);

  const mapFirebaseCase = (caseId, caseData = {}, extraFields = {}) => ({
    id: caseId,
    ...caseData,
    dueDateMillis: caseData.dueDate,
    creationDateMillis: caseData.creationDate,
    expirationDateMillis: caseData.expirationDate,
    dueDate: formatDateFromMillis(caseData.dueDate),
    creationDate: formatDateFromMillis(caseData.creationDate),
    expirationDate: formatDateFromMillis(caseData.expirationDate),
    ...extraFields,
  });

  const toggleViewMode = () => {
    const newMode = viewMode === "cards" ? "list" : "cards";
    setViewMode(newMode);
    localStorage.setItem("caseView", newMode);
  };

  useEffect(() => {
    if (user.isSignedIn) {
      const userRef = ref(database, `users/${user.currentUid}`);
      onValue(userRef, (snapshot) => {
        const data = snapshot.val();
        dispatch(setCurrentUser(data));
      });

      return () => {
        off(userRef);
      };
    }
  }, [dispatch, user.currentUid, user.isSignedIn]);

  // Calculate total pages based on user cases
  // Cases is now a map where keys are timestamps (milliseconds since epoch)
  const casesMap = user.currentUser?.cases || {};
  const totalCases = Object.keys(casesMap).length;
  const totalPages = Math.ceil(totalCases / itemsPerPage);

  // Reset to page 1 when admin view mode changes or when cases list changes length or page size changes
  useEffect(() => {
    setCurrentPage(1);
  }, [adminViewMode, totalCases, itemsPerPage]);

  useEffect(() => {
    const fetchCases = async () => {
      setIsLoading(true);
      try {
        if (user.isAdmin && adminViewMode !== "none") {
          const casesRef = ref(database, "cases");
          const snapshot = await get(casesRef);
          const fetchedCases = [];
          const cacheUpdates = {};
          const titleUpdates = {};

          if (snapshot.exists()) {
            const allCases = snapshot.val();
            const statusToFilter =
              adminViewMode === "inProgress" ? "IN PROGRESS" : "READY";

            Object.entries(allCases).forEach(([caseId, caseData]) => {
              if (caseData.status === statusToFilter) {
                const formattedCase = mapFirebaseCase(caseId, caseData, {
                  isAdminView: true,
                });
                fetchedCases.push(formattedCase);
                cacheUpdates[caseId] = formattedCase;
                // Mark as fetched to prevent duplicate requests from preloadTitles
                fetchedTitleIdsRef.current.add(caseId);
                const title = caseData.title || "";
                titleUpdates[caseId] = {
                  title,
                  searchable: title.toLowerCase(),
                };
              }
            });
          }

          fetchedCases.sort((a, b) => a.title.localeCompare(b.title));
          if (Object.keys(cacheUpdates).length) {
            setCaseCache((prev) => ({ ...prev, ...cacheUpdates }));
          }
          if (Object.keys(titleUpdates).length) {
            setCaseTitleIndex((prev) => ({ ...prev, ...titleUpdates }));
          }
          setCaseList(fetchedCases);
        } else if (user.currentUser?.cases) {
          // Cases is now a map where keys are timestamps (milliseconds since epoch)
          // and values are case IDs
          const casesMap = user.currentUser.cases;

          // Convert map to array of [timestamp, caseId] entries
          const caseEntries = Object.entries(casesMap).map(
            ([timestamp, caseId]) => [parseInt(timestamp, 10), caseId]
          );
          // Sort by timestamp (keys) in descending order (newest first)
          caseEntries.sort((a, b) => b[0] - a[0]);

          // Calculate pagination indices
          const startIndex = (currentPage - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;

          // Get only the case entries for the current page
          const paginatedCaseEntries = caseEntries.slice(startIndex, endIndex);
          const fetchedCases = [];
          const cacheUpdates = {};
          const titleUpdates = {};

          // Fetch only the cases for current page
          for (const [, caseId] of paginatedCaseEntries) {
            const caseRef = ref(database, `cases/${caseId}`);
            const snapshot = await get(caseRef);

            if (snapshot.exists()) {
              const caseData = snapshot.val();
              const formattedCase = mapFirebaseCase(caseId, caseData);
              fetchedCases.push(formattedCase);
              cacheUpdates[caseId] = formattedCase;
              // Mark as fetched to prevent duplicate requests from preloadTitles
              fetchedTitleIdsRef.current.add(caseId);
              const title = caseData.title || "";
              titleUpdates[caseId] = {
                title,
                searchable: title.toLowerCase(),
              };
            }
          }

          if (Object.keys(cacheUpdates).length) {
            setCaseCache((prev) => ({ ...prev, ...cacheUpdates }));
          }
          if (Object.keys(titleUpdates).length) {
            setCaseTitleIndex((prev) => ({ ...prev, ...titleUpdates }));
          }
          // Cases are already sorted by timestamp (newest first), so no need to sort again
          setCaseList(fetchedCases);
        } else {
          setCaseList([]);
        }
      } catch (error) {
        console.error("Error fetching cases:", error);
        setCaseList([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCases();
  }, [
    user.currentUser,
    user.isAdmin,
    adminViewMode,
    currentPage,
    itemsPerPage,
  ]);

  // Memoized function to preload titles - only fetches titles not already fetched
  const preloadMissingTitles = useCallback(async () => {
    if (!user.currentUser?.cases) return;

    const casesMap = user.currentUser.cases;
    const caseIds = Object.values(casesMap);
    
    // Filter out IDs that have already been fetched or are currently being fetched
    const missingCaseIds = caseIds.filter(
      (caseId) => !fetchedTitleIdsRef.current.has(caseId)
    );
    
    if (!missingCaseIds.length) return;

    // Mark these IDs as being fetched to prevent duplicate requests
    missingCaseIds.forEach((id) => fetchedTitleIdsRef.current.add(id));

    try {
      const updates = {};
      await Promise.all(
        missingCaseIds.map(async (caseId) => {
          const titleRef = ref(database, `cases/${caseId}/title`);
          const snapshot = await get(titleRef);
          const title = snapshot.exists() ? snapshot.val() || "" : "";
          updates[caseId] = {
            title,
            searchable: title.toLowerCase(),
          };
        })
      );

      if (Object.keys(updates).length) {
        setCaseTitleIndex((prev) => ({ ...prev, ...updates }));
      }
    } catch (error) {
      console.error("Error preloading case titles:", error);
      // On error, remove failed IDs from the set so they can be retried
      missingCaseIds.forEach((id) => fetchedTitleIdsRef.current.delete(id));
    }
  }, [user.currentUser?.cases]);

  // Reset fetched IDs when user/cases change completely
  useEffect(() => {
    if (!user.currentUser?.cases) {
      setCaseTitleIndex({});
      fetchedTitleIdsRef.current.clear();
      hasSearchedRef.current = false;
    }
  }, [user.currentUser?.cases]);

  useEffect(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      setSearchResults([]);
      setIsSearchLoading(false);
      return;
    }

    // Lazy load: Only start preloading titles when user first searches
    if (!hasSearchedRef.current) {
      hasSearchedRef.current = true;
      preloadMissingTitles();
    }

    let isCancelled = false;

    const runSearch = async () => {
      setIsSearchLoading(true);
      try {
        // Use caseTitleIndex directly - react will re-run this effect when it changes
        const matchingIds = Object.entries(caseTitleIndex)
          .filter(([, { searchable }]) => searchable.includes(normalizedQuery))
          .map(([caseId]) => caseId);

        if (!matchingIds.length) {
          if (!isCancelled) {
            setSearchResults([]);
          }
          return;
        }

        // Use caseCache directly
        const missingIds = matchingIds.filter((caseId) => !caseCache[caseId]);
        const newCacheEntries = {};

        if (missingIds.length) {
          await Promise.all(
            missingIds.map(async (caseId) => {
              const caseRef = ref(database, `cases/${caseId}`);
              const snapshot = await get(caseRef);
              if (snapshot.exists()) {
                newCacheEntries[caseId] = mapFirebaseCase(
                  caseId,
                  snapshot.val()
                );
              }
            })
          );
        }

        if (!isCancelled) {
          // Update cache if we fetched new entries
          if (Object.keys(newCacheEntries).length) {
            setCaseCache((prev) => ({ ...prev, ...newCacheEntries }));
          }
          // Compute results from both existing cache and new entries
          const combinedCache = { ...caseCache, ...newCacheEntries };
          const results = matchingIds
            .map((caseId) => combinedCache[caseId])
            .filter(Boolean);
          setSearchResults(results);
        }
      } catch (error) {
        console.error("Error searching cases:", error);
        if (!isCancelled) {
          setSearchResults([]);
        }
      } finally {
        if (!isCancelled) {
          setIsSearchLoading(false);
        }
      }
    };

    // Debounce search to avoid too many rapid requests
    const debounceTimeout = setTimeout(runSearch, 300);

    return () => {
      isCancelled = true;
      clearTimeout(debounceTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, caseTitleIndex, caseCache, preloadMissingTitles]);

  const handleHardDelete = async () => {
    if (!caseToDelete) return;

    try {
      const caseRef = ref(database, `cases/${caseToDelete}`);
      await remove(caseRef);

      // Cases is now a map, so we need to remove the entry by caseId (value)
      const casesMap = user.currentUser.cases || {};
      const newCasesMap = {};

      // Remove the caseId from the map
      Object.entries(casesMap).forEach(([timestamp, caseId]) => {
        if (caseId !== caseToDelete) {
          newCasesMap[timestamp] = caseId;
        }
      });

      const userRef = ref(database, `users/${user.currentUid}/cases`);
      await set(userRef, newCasesMap);

      const fullCaseToDelete = caseList.find(
        (caseItem) => caseItem.id === caseToDelete
      );

      const userIdsToUpdate = Object.keys(fullCaseToDelete.users).filter(
        (userId) => userId !== user.currentUid
      );

      const updateOtherUsersPromises = userIdsToUpdate.map(async (userId) => {
        const userSnapshot = await get(ref(database, `users/${userId}`));
        const userData = userSnapshot.val();

        if (userData?.cases) {
          const otherUserCasesMap = userData.cases;
          const updatedCases = {};

          // Remove the caseId from the map
          Object.entries(otherUserCasesMap).forEach(([timestamp, caseId]) => {
            if (caseId !== caseToDelete) {
              updatedCases[timestamp] = caseId;
            }
          });

          await set(ref(database, `users/${userId}/cases`), updatedCases);
        }
      });

      await Promise.all(updateOtherUsersPromises);

      // Fetch caseDetails to delete files from storage
      const caseDetailRef = ref(database, `caseDetails/${caseToDelete}`);
      const caseDetailSnapshot = await get(caseDetailRef);
      const caseDetail = caseDetailSnapshot.val();

      if (caseDetail?.modelInfos && caseDetail.modelInfos.length > 0) {
        const deletePromises = caseDetail.modelInfos.map(async (info) => {
          const fileRef = storageRef(
            storage,
            `cases/${caseToDelete}/stlFiles/${info.storageName}`
          );
          await deleteObject(fileRef);
        });

        await Promise.all(deletePromises);
      }

      if (caseDetail?.dicoms && caseDetail.dicoms.length > 0) {
        const deletePromises = caseDetail.dicoms.map(async (info) => {
          const fileRef = storageRef(
            storage,
            `cases/${caseToDelete}/dicomFiles/${info.storageName}`
          );
          await deleteObject(fileRef);
        });

        await Promise.all(deletePromises);
      }

      // Delete the caseDetails document
      await remove(caseDetailRef);

      setCaseList((prevCaseList) =>
        prevCaseList.filter((caseItem) => caseItem.id !== caseToDelete)
      );

      setCaseCache((prev) => {
        if (!prev[caseToDelete]) return prev;
        const updated = { ...prev };
        delete updated[caseToDelete];
        return updated;
      });
      setCaseTitleIndex((prev) => {
        if (!prev[caseToDelete]) return prev;
        const updated = { ...prev };
        delete updated[caseToDelete];
        return updated;
      });
      setSearchResults((prev) =>
        prev.filter((caseItem) => caseItem.id !== caseToDelete)
      );

      setCaseToDelete(null);
      setIsHardDeleteModalOpen(false);
      setShowDeleteButtons(false);
    } catch (err) {
      console.error("Error deleting case:", err);
    }
  };

  const handleSoftDelete = async () => {
    if (!caseToDelete) return;

    try {
      // Cases is now a map, so we need to remove the entry by caseId (value)
      const casesMap = user.currentUser.cases || {};
      const newCasesMap = {};

      // Remove the caseId from the map
      Object.entries(casesMap).forEach(([timestamp, caseId]) => {
        if (caseId !== caseToDelete) {
          newCasesMap[timestamp] = caseId;
        }
      });

      const userRef = ref(database, `users/${user.currentUid}/cases`);
      await set(userRef, newCasesMap);

      const caseUserRef = ref(
        database,
        `cases/${caseToDelete}/users/${user.currentUid}`
      );
      await remove(caseUserRef);

      setCaseList((prevCaseList) =>
        prevCaseList.filter((caseItem) => caseItem.id !== caseToDelete)
      );

      setCaseCache((prev) => {
        if (!prev[caseToDelete]) return prev;
        const updated = { ...prev };
        delete updated[caseToDelete];
        return updated;
      });
      setCaseTitleIndex((prev) => {
        if (!prev[caseToDelete]) return prev;
        const updated = { ...prev };
        delete updated[caseToDelete];
        return updated;
      });
      setSearchResults((prev) =>
        prev.filter((caseItem) => caseItem.id !== caseToDelete)
      );

      setCaseToDelete(null);
      setIsSoftDeleteModalOpen(false);
    } catch (err) {
      console.error("Error leaving case:", err);
    }
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const getSortDirection = (key) =>
    sortConfig.key === key
      ? sortConfig.direction === "ascending"
        ? "▲"
        : "▼"
      : "";

  const hasSearchQuery = searchQuery.trim().length > 0;
  const baseCaseList = hasSearchQuery ? searchResults : caseList;

  const sortedCaseList = [...baseCaseList]
    .filter((caseItem) =>
      caseItem.title?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (!sortConfig.key) return 0;

      // Handle date fields that have millisecond values for proper sorting
      const dateFields = {
        dueDate: "dueDateMillis",
        creationDate: "creationDateMillis",
        expirationDate: "expirationDateMillis",
      };

      const sortKey = dateFields[sortConfig.key] || sortConfig.key;
      const valueA = a[sortKey];
      const valueB = b[sortKey];

      if (!valueA && !valueB) return 0;
      if (!valueA) return 1;
      if (!valueB) return -1;

      // For date fields (milliseconds), compare numerically
      if (dateFields[sortConfig.key]) {
        const numA = typeof valueA === "number" ? valueA : parseInt(valueA, 10);
        const numB = typeof valueB === "number" ? valueB : parseInt(valueB, 10);
        if (isNaN(numA) || isNaN(numB)) return 0;
        return sortConfig.direction === "ascending" ? numA - numB : numB - numA;
      }

      // For other fields, use string comparison
      const strA = String(valueA || "");
      const strB = String(valueB || "");
      return sortConfig.direction === "ascending"
        ? strA.localeCompare(strB)
        : strB.localeCompare(strA);
    });

  const openHardDeleteModal = (caseId) => {
    setCaseToDelete(caseId);
    setIsHardDeleteModalOpen(true);
  };

  const openSoftDeleteModal = (caseId) => {
    setCaseToDelete(caseId);
    setIsSoftDeleteModalOpen(true);
  };

  const closeModals = () => {
    setIsHardDeleteModalOpen(false);
    setIsSoftDeleteModalOpen(false);
    setCaseToDelete(null);
  };

  const handleAddCase = () => {
    dispatch(setSelectedCase(null));
    navigate("/addoreditcasepage");
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setIsLoading(true);
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const PaginationControls = () => {
    if (totalPages <= 1) return null;

    const btnBase = {
      padding: "6px 12px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: 500,
      transition: "all 0.15s",
      cursor: "pointer",
      border: "1px solid #E2E8F0",
      backgroundColor: "#F8FAFC",
      color: "#475569",
    };

    const btnDisabled = { ...btnBase, opacity: 0.4, cursor: "not-allowed" };
    const btnActive = { ...btnBase, backgroundColor: "#30A69C", border: "none", color: "#fff", fontWeight: 600 };

    return (
      <div className="mt-6 flex items-center justify-center gap-2">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={currentPage === 1 ? btnDisabled : btnBase}
          onMouseEnter={(e) => { if (currentPage !== 1) { e.currentTarget.style.backgroundColor = "#E2E8F0"; e.currentTarget.style.color = "#0F172A"; }}}
          onMouseLeave={(e) => { if (currentPage !== 1) { e.currentTarget.style.backgroundColor = "#F8FAFC"; e.currentTarget.style.color = "#475569"; }}}
        >
          Previous
        </button>

        <div className="flex items-center gap-2">
          {currentPage > 2 && (
            <>
              <button onClick={() => handlePageChange(1)} style={btnBase}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#E2E8F0"; e.currentTarget.style.color = "#0F172A"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#F8FAFC"; e.currentTarget.style.color = "#475569"; }}>1</button>
              {currentPage > 3 && <span style={{ color: "#94A3B8" }}>...</span>}
            </>
          )}

          {currentPage > 1 && (
            <button onClick={() => handlePageChange(currentPage - 1)} style={btnBase}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#E2E8F0"; e.currentTarget.style.color = "#0F172A"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#F8FAFC"; e.currentTarget.style.color = "#475569"; }}>
              {currentPage - 1}
            </button>
          )}

          <div style={btnActive}>{currentPage}</div>

          {currentPage < totalPages && (
            <button onClick={() => handlePageChange(currentPage + 1)} style={btnBase}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#E2E8F0"; e.currentTarget.style.color = "#0F172A"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#F8FAFC"; e.currentTarget.style.color = "#475569"; }}>
              {currentPage + 1}
            </button>
          )}

          {currentPage < totalPages - 1 && (
            <>
              {currentPage < totalPages - 2 && <span style={{ color: "#94A3B8" }}>...</span>}
              <button onClick={() => handlePageChange(totalPages)} style={btnBase}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#E2E8F0"; e.currentTarget.style.color = "#0F172A"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#F8FAFC"; e.currentTarget.style.color = "#475569"; }}>
                {totalPages}
              </button>
            </>
          )}
        </div>

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={currentPage === totalPages ? btnDisabled : btnBase}
          onMouseEnter={(e) => { if (currentPage !== totalPages) { e.currentTarget.style.backgroundColor = "#E2E8F0"; e.currentTarget.style.color = "#0F172A"; }}}
          onMouseLeave={(e) => { if (currentPage !== totalPages) { e.currentTarget.style.backgroundColor = "#F8FAFC"; e.currentTarget.style.color = "#475569"; }}}
        >
          Next
        </button>

        <div className="ml-4 text-sm" style={{ color: "#94A3B8" }}>
          Page <span className="font-semibold" style={{ color: "#475569" }}>{currentPage}</span> of{" "}
          <span className="font-semibold" style={{ color: "#475569" }}>{totalPages}</span>{" "}
          <span style={{ color: "#94A3B8" }}>({totalCases} total cases)</span>
        </div>
      </div>
    );
  };

  return (
    <div className={isMobile ? "p-4" : "p-8"} style={{ minHeight: "100vh", backgroundColor: "#0B0E13" }}>
      {/* Main white content card */}
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "16px",
          padding: isMobile ? "20px" : "28px 32px",
          boxShadow: "0 4px 32px rgba(0,0,0,0.4)",
          minHeight: "calc(100vh - 80px)",
        }}
      >
      {/* Header with inline per-page buttons */}
      <div
        className="flex flex-row justify-between items-center mb-4 gap-2"
        style={{
          "--main-color": anatomyConstants.mainColor,
          "--main-color-bold": anatomyConstants.mainColorBold,
        }}
      >
        <h2 className="text-lg font-semibold" style={{ color: "#0F172A" }}>
          Case List
          {user.isAdmin &&
            adminViewMode === "inProgress" &&
            " (Admin View - All In-Progress Cases)"}
          {user.isAdmin &&
            adminViewMode === "ready" &&
            " (Admin View - All Ready Cases)"}
        </h2>

        <div className="flex flex-row items-center gap-3 ml-auto">
          {user.isAdmin && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() =>
                  setAdminViewMode(
                    adminViewMode === "inProgress" ? "none" : "inProgress"
                  )
                }
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                style={{
                  backgroundColor: adminViewMode === "inProgress" ? "rgba(139,92,246,0.25)" : "rgba(139,92,246,0.1)",
                  border: "1px solid rgba(139,92,246,0.3)",
                  color: "#A78BFA",
                }}
              >
                {adminViewMode === "inProgress" ? "View My Cases" : "View All In-Progress Cases"}
              </button>
              <button
                onClick={() =>
                  setAdminViewMode(adminViewMode === "ready" ? "none" : "ready")
                }
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                style={{
                  backgroundColor: adminViewMode === "ready" ? "rgba(48,166,156,0.25)" : "rgba(48,166,156,0.1)",
                  border: "1px solid rgba(48,166,156,0.3)",
                  color: "#30A69C",
                }}
              >
                {adminViewMode === "ready" ? "View My Cases" : "View All Ready Cases"}
              </button>
            </div>
          )}

          <div className="flex flex-row items-center gap-2 text-sm" style={{ color: "#475569" }}>
            <span>Cases per page:</span>
            {pageSizeOptions.map((size) => (
              <button
                key={size}
                onClick={() => {
                  setItemsPerPage(size);
                  localStorage.setItem("caseItemsPerPage", String(size));
                }}
                className="px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200"
                style={{
                  backgroundColor: size === itemsPerPage ? "#30A69C" : "#F1F5F9",
                  border: size === itemsPerPage ? "none" : "1px solid #E2E8F0",
                  color: size === itemsPerPage ? "#fff" : "#64748B",
                }}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      </div>

      <CaseSearchBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onAddCase={handleAddCase}
        toggleDeleteMode={() => setShowDeleteButtons(!showDeleteButtons)}
        isDeleteModeActive={showDeleteButtons}
        isMobile={isMobile}
        viewMode={viewMode}
        toggleViewMode={toggleViewMode}
      />

      {isLoading || isSearchLoading ? (
        <div
          className="flex justify-center py-16"
          style={{
            "--main-color": anatomyConstants.mainColor,
          }}
        >
          <div className="flex items-center gap-3" style={{ color: "#64748B" }}>
            <div className="h-6 w-6 border-2 rounded-full animate-spin" style={{ borderColor: "rgba(255,255,255,0.12)", borderTopColor: "#30A69C" }}></div>
            <span>
              {isSearchLoading
                ? "Searching cases across all pages..."
                : `Loading cases ${
                    (currentPage - 1) * itemsPerPage + 1
                  } / ${Math.min(currentPage * itemsPerPage, totalCases)}...`}
            </span>
          </div>
        </div>
      ) : isMobile ? (
        <MobileCaseTable
          sortedCaseList={sortedCaseList}
          navigate={navigate}
          showDeleteButtons={showDeleteButtons}
          openHardDeleteModal={openHardDeleteModal}
          openSoftDeleteModal={openSoftDeleteModal}
          user={user.currentUser}
        />
      ) : viewMode === "list" ? (
        <CaseListTable
          caseList={sortedCaseList}
          requestSort={requestSort}
          getSortDirection={getSortDirection}
          showDeleteButtons={showDeleteButtons}
          openHardDeleteModal={openHardDeleteModal}
          openSoftDeleteModal={openSoftDeleteModal}
          navigate={navigate}
          user={user.currentUser}
        />
      ) : (
        <CaseTable
          caseList={sortedCaseList}
          showDeleteButtons={showDeleteButtons}
          openHardDeleteModal={openHardDeleteModal}
          openSoftDeleteModal={openSoftDeleteModal}
          navigate={navigate}
          user={user.currentUser}
        />
      )}

      {isHardDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isHardDeleteModalOpen}
          closeModal={closeModals}
          handleDelete={handleHardDelete}
          entityName="this case from everyone"
        />
      )}
      {isSoftDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isSoftDeleteModalOpen}
          closeModal={closeModals}
          handleDelete={handleSoftDelete}
          entityName="your access to this case"
        />
      )}

      {!hasSearchQuery && <PaginationControls />}
      </div>
    </div>
  );
};

export default CaseListPageView;
